import PropTypes from 'prop-types';
import {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import cookie from '../../../../cookie';
import {setPageMessage} from '../../../actions/pageMessageActions';
import {recordBigQueryEvent} from '../../../actions/paymentActions/threedsActions';
import {stopOrderProcessing, threeDSecureSuccess} from '../../../actions/site-actions';
import Translation from '../../../lang/Translation';
import {getSettings} from '../../../selectors/selectors';
import getThreeDSecureData from '../../../selectors/ThreeDSSelectors/getThreeDSecureData';
import Track from '../../../utils/tracking';
import Spinner from '../../ui/Spinner';
import PostToIframe from '../ProviderAgnostic/PostToIframe';
import useStyles from '../ThreeDSecure.styles';

export const THREE_D_SECURE_API_RETURN_URL = `https://${window.location.host}/checkout/3ds-confirmation`;
const PROVIDER = 'CHASE';

export function Chase3DSRedirect(props) {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (loading) {
      try {
        recordBigQueryEvent('1ChallengeRequired', PROVIDER);
      } catch (error) {
        Track.track(error);
      }
    }
  }, []);
  
  const handleOnLoadComplete = (event) => {
    try {
      // This only works once the user has returned to our site. Otherwise a cross-origin error is thrown.
      const loadedURL = event.target.contentWindow.location.href;
      
      if (loadedURL === THREE_D_SECURE_API_RETURN_URL) {
        props.threeDSecureSuccess();
        try {
          recordBigQueryEvent('1ChallengeCompleted', PROVIDER, [{name: 'result', value: 'success'}], []);
        } catch (error) {
          Track.track(error);
        }
      } else {
        try {
          recordBigQueryEvent('1ChallengeInitiated', PROVIDER);
        } catch (error) {
          Track.track(error);
        }
      }
    } catch (exception) {
      // Couldn't determine iframe's location, likely because it's still on the bank's domain'
      try {
        recordBigQueryEvent('1ChallengeInitiated', PROVIDER);
      } catch (error) {
        Track.track(error);
      }
    }
    
    setLoading(false);
  };
  
  const handleOnError = () => {
    // todo: check with UX about what should happen here and provide translations if appropriate
    const errorMessage = 'Error loading 3DS Issuer frame';
    Track.track(new Error(errorMessage));
    props.setPageMessage('Sorry, we couldn\'t contact your bank to verify your payment. Please try again.');
    props.stopOrderProcessing();
    try {
      recordBigQueryEvent('1ChallengeCompleted', PROVIDER,
        [{name: 'result', value: 'error'}], [{type: 'loading', value: errorMessage}]);
    } catch (error) {
      Track.track(error);
    }
  };
  
  const threeDSecureData = {
    issuerURL: props.issuerURL,
    PaReq: props.oneTime3DSToken,
    TermUrl: THREE_D_SECURE_API_RETURN_URL,
    MD: props.use3dsSessionId ? props.threeDSecureData?.sessionId : cookie.session(),
  };
  
  return (
    <div className={classes.ThreeDSecureContainer} data-test="Chase3DSRedirect">
      {loading && (
        <div className={classes.loading}>
          <Spinner size="small" />
          <br />
          <Translation>loading.security.check</Translation>
        </div>
      )}
    
      <PostToIframe
        postURL={threeDSecureData.issuerURL}
        formFields={{
          PaReq: threeDSecureData.PaReq,
          TermUrl: threeDSecureData.TermUrl,
          MD: threeDSecureData.MD,
        }}
        onError={handleOnError}
        onLoad={handleOnLoadComplete}
      />
    </div>
  );
}

Chase3DSRedirect.propTypes = {
  issuerURL: PropTypes.string.isRequired,
  oneTime3DSToken: PropTypes.string.isRequired,
  merchantData: PropTypes.string.isRequired,
  verificationProvider: PropTypes.string,
  
  // Actions
  threeDSecureSuccess: PropTypes.func.isRequired,
  stopOrderProcessing: PropTypes.func.isRequired,
  setPageMessage: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  threeDSecureData: PropTypes.object,
  use3dsSessionId: PropTypes.bool,
};

const mapStateToProps = state => ({
  threeDSecureData: getThreeDSecureData(state) || null,
  use3dsSessionId: getSettings(state)?.use_3ds_session_id || false,
});

const mapDispatchToProps = {
  threeDSecureSuccess,
  stopOrderProcessing,
  setPageMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(Chase3DSRedirect);
